import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { listCognitoUsers, CognitoUser } from '../services/adminService';
import { listUserDocuments, uploadDocument, getDocumentUrl, deleteDocument, formatFileSize, Document, DocumentCategory } from '../services/documentService';
import {
  adminGetEmergencyStatus,
  adminGenerateEmergencyCode,
  adminRevokeEmergencyCode,
  adminGetAuditLog,
} from '../services/emergencyService';
import type { HealthSummaryData } from '../types/healthSummary';
import type {
  EmergencyTokenStatus,
  EmergencyAuditEntry,
  GenerateEmergencyCodeResponse,
} from '../types/emergency';
import {
  adminListHealthUpdates,
  adminSubmitHealthUpdate,
  adminUpdateHealthUpdate,
  adminDeleteHealthUpdate,
  adminGetUpdateAuditLog,
  adminGetArchivedUpdates,
} from '../services/healthUpdateService';
import type { HealthUpdate, ArchivedHealthUpdate, AllCategory, AllCategorySchema, HealthUpdateAuditEntry } from '../types/healthUpdate';
import { ALL_CATEGORIES } from '../types/healthUpdate';
import './Admin.css';

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState<CognitoUser | null>(null);
  const [filter, setFilter] = useState<'all' | 'admins' | 'users'>('all');

  // Document management modal state
  const [showDocModal, setShowDocModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState<DocumentCategory>('health-summary');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Health summary preview state
  const [previewData, setPreviewData] = useState<HealthSummaryData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Emergency access modal state
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyStatus, setEmergencyStatus] = useState<EmergencyTokenStatus | null>(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState('');
  const [emergencyGenerated, setEmergencyGenerated] = useState<GenerateEmergencyCodeResponse | null>(null);
  const [emergencyAuditLog, setEmergencyAuditLog] = useState<EmergencyAuditEntry[]>([]);
  const [showEmergencyAudit, setShowEmergencyAudit] = useState(false);

  // Patient updates modal state
  const [showUpdatesModal, setShowUpdatesModal] = useState(false);
  const [patientUpdates, setPatientUpdates] = useState<HealthUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [updatesError, setUpdatesError] = useState('');
  const [updatesFilter, setUpdatesFilter] = useState('');

  // Admin add/edit form state
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<HealthUpdate | null>(null);
  const [adminFormCategory, setAdminFormCategory] = useState<AllCategorySchema | null>(null);
  const [adminFormData, setAdminFormData] = useState<Record<string, string>>({});
  const [adminFormSaving, setAdminFormSaving] = useState(false);

  // Health update audit log state
  const [showUpdateAuditLog, setShowUpdateAuditLog] = useState(false);
  const [updateAuditLog, setUpdateAuditLog] = useState<HealthUpdateAuditEntry[]>([]);
  const [updateAuditLoading, setUpdateAuditLoading] = useState(false);

  // Archived updates state
  const [showArchivedUpdates, setShowArchivedUpdates] = useState(false);
  const [archivedUpdates, setArchivedUpdates] = useState<ArchivedHealthUpdate[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const cognitoUsers = await listCognitoUsers();
      setUsers(cognitoUsers);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(
        err.message || 'Failed to load users. Make sure you have admin permissions.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (category: DocumentCategory) => {
    if (!selectedUser) return;
    setDocsLoading(true);
    setDocsError('');
    try {
      const docs = await listUserDocuments(user?.userId || '', selectedUser.username, category);
      setDocuments(docs);
    } catch (err: any) {
      setDocsError(err.message || 'Failed to load documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const handleOpenDocModal = () => {
    setShowDocModal(true);
    setActiveCategory('health-summary');
    fetchDocuments('health-summary');
  };

  const handleCategoryChange = (category: DocumentCategory) => {
    setActiveCategory(category);
    setPreviewData(null);
    fetchDocuments(category);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedUser) return;

    setUploading(true);
    setDocsError('');

    try {
      for (const file of Array.from(files)) {
        await uploadDocument(user?.userId || '', file, selectedUser.username, activeCategory);
      }
      await fetchDocuments(activeCategory);
    } catch (err: any) {
      setDocsError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const url = await getDocumentUrl(doc.key);
      window.open(url, '_blank');
    } catch (err: any) {
      setDocsError(err.message || 'Failed to download file');
    }
  };

  const handleDeleteDoc = async (doc: Document) => {
    if (!window.confirm(`Are you sure you want to delete ${doc.name}?`)) return;

    try {
      await deleteDocument(doc.key);
      await fetchDocuments(activeCategory);
    } catch (err: any) {
      setDocsError(err.message || 'Failed to delete file');
    }
  };

  const handleViewHealthSummary = async (doc: Document) => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const url = await getDocumentUrl(doc.key);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const XLSX = await import('xlsx');
      (window as any).__XLSX__ = XLSX;
      const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });

      const findSheet = (wb: any, ...names: string[]) => {
        for (const n of wb.SheetNames) {
          const norm = n.trim().toLowerCase();
          for (const c of names) {
            if (norm === c.toLowerCase()) return wb.Sheets[n];
          }
        }
        return null;
      };
      const parseFieldValue = (sheet: any) => {
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (rows.length < 2) return [];
        const headers = rows[0].map((h: any) => String(h).trim().toLowerCase());
        const fi = headers.indexOf('field');
        const vi = headers.indexOf('value');
        if (fi === -1 || vi === -1) return [];
        return rows.slice(1).filter((r) => r[fi]).map((r) => ({
          field: String(r[fi] ?? '').trim(),
          value: String(r[vi] ?? '').trim(),
        }));
      };
      const parseTable = (sheet: any) => {
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (rows.length < 2) return { headers: [] as string[], rows: [] as string[][] };
        const headers = rows[0].map((h: any) => String(h ?? '').trim());
        const dataRows = rows.slice(1)
          .filter((r) => r.some((c: any) => c != null && String(c).trim()))
          .map((r) => headers.map((_, i) => String(r[i] ?? '').trim()));
        return { headers, rows: dataRows };
      };

      // Parse all sheets into a generic structure
      const personalSheet = findSheet(workbook, 'Personal Identification', 'Personal Info');
      const demoSheet = findSheet(workbook, 'Demographics');
      const emergencySheet = findSheet(workbook, 'Emergency Contacts');
      const familyDocSheet = findSheet(workbook, 'Family Doctor');
      const conditionsSheet = findSheet(workbook, 'Active Medical Conditions', 'Conditions');
      const allergiesSheet = findSheet(workbook, 'Allergies & Adverse Reaction', 'Allergies');
      const medsSheet = findSheet(workbook, 'Medications');
      const vaccineSheet = findSheet(workbook, 'Vaccine History');
      const diagnosticSheet = findSheet(workbook, 'Diagnostic History');
      const surgicalSheet = findSheet(workbook, 'Surgical Hx & Hospitalization');
      const travelSheet = findSheet(workbook, 'Travel Hx');
      const insuranceSheet = findSheet(workbook, 'Insurance & Care Planning');
      const familyHxSheet = findSheet(workbook, 'Family Hx');
      const careTeamSheet = findSheet(workbook, 'Care Team');

      // Store as a simple preview structure using HealthSummaryData shape
      // We'll use a simplified rendering approach
      const preview: any = {
        sections: [] as { title: string; type: 'grid' | 'table'; data: any }[],
      };
      if (personalSheet) {
        const fv = parseFieldValue(personalSheet);
        if (fv.length) preview.sections.push({ title: 'Personal Identification', type: 'grid', data: fv });
      }
      if (demoSheet) {
        const fv = parseFieldValue(demoSheet);
        if (fv.length) preview.sections.push({ title: 'Demographics', type: 'grid', data: fv });
      }
      const tableSheets = [
        { sheet: emergencySheet, title: 'Emergency Contacts' },
        { sheet: familyDocSheet, title: 'Family Doctor & Pharmacy' },
        { sheet: conditionsSheet, title: 'Active Medical Conditions' },
        { sheet: allergiesSheet, title: 'Allergies & Adverse Reactions' },
        { sheet: medsSheet, title: 'Medications' },
        { sheet: vaccineSheet, title: 'Vaccine History' },
        { sheet: diagnosticSheet, title: 'Diagnostic History' },
        { sheet: surgicalSheet, title: 'Surgical History & Hospitalization' },
        { sheet: travelSheet, title: 'Travel History' },
      ];
      for (const { sheet, title } of tableSheets) {
        if (sheet) {
          const t = parseTable(sheet);
          if (t.rows.length) preview.sections.push({ title, type: 'table', data: t });
        }
      }
      if (insuranceSheet) {
        const fv = parseFieldValue(insuranceSheet);
        if (fv.length) preview.sections.push({ title: 'Insurance & Care Planning', type: 'grid', data: fv });
      }
      const moreTableSheets = [
        { sheet: familyHxSheet, title: 'Family History' },
        { sheet: careTeamSheet, title: 'Care Team' },
      ];
      for (const { sheet, title } of moreTableSheets) {
        if (sheet) {
          const t = parseTable(sheet);
          if (t.rows.length) preview.sections.push({ title, type: 'table', data: t });
        }
      }

      setPreviewData(preview);
    } catch (err: any) {
      setDocsError(err.message || 'Failed to load health summary preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Emergency access handlers
  const handleOpenEmergencyModal = async () => {
    if (!selectedUser) return;
    setShowEmergencyModal(true);
    setEmergencyError('');
    setEmergencyGenerated(null);
    setShowEmergencyAudit(false);
    setEmergencyLoading(true);
    try {
      const status = await adminGetEmergencyStatus(selectedUser.username);
      setEmergencyStatus(status);
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to load emergency status');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleAdminGenerate = async () => {
    if (!selectedUser) return;
    setEmergencyLoading(true);
    setEmergencyError('');
    try {
      const result = await adminGenerateEmergencyCode(selectedUser.username);
      setEmergencyGenerated(result);
      const status = await adminGetEmergencyStatus(selectedUser.username);
      setEmergencyStatus(status);
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to generate emergency code');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleAdminRevoke = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Revoke emergency code for ${selectedUser.email || selectedUser.username}?`)) return;
    setEmergencyLoading(true);
    setEmergencyError('');
    try {
      await adminRevokeEmergencyCode(selectedUser.username);
      setEmergencyGenerated(null);
      const status = await adminGetEmergencyStatus(selectedUser.username);
      setEmergencyStatus(status);
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to revoke emergency code');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleAdminViewAudit = async () => {
    if (!selectedUser) return;
    if (showEmergencyAudit) {
      setShowEmergencyAudit(false);
      return;
    }
    setEmergencyLoading(true);
    try {
      const result = await adminGetAuditLog(selectedUser.username);
      setEmergencyAuditLog(result.entries);
      setShowEmergencyAudit(true);
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to load audit log');
    } finally {
      setEmergencyLoading(false);
    }
  };

  // Patient updates handlers
  const handleOpenUpdatesModal = async () => {
    if (!selectedUser) return;
    setShowUpdatesModal(true);
    setUpdatesError('');
    setUpdatesFilter('');
    setShowUpdateAuditLog(false);
    setShowArchivedUpdates(false);
    setUpdatesLoading(true);
    try {
      const result = await adminListHealthUpdates(selectedUser.username);
      setPatientUpdates(result.updates);
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to load patient updates');
    } finally {
      setUpdatesLoading(false);
    }
  };

  const handleUpdatesFilterChange = async (category: string) => {
    if (!selectedUser) return;
    setUpdatesFilter(category);
    setUpdatesLoading(true);
    try {
      const result = await adminListHealthUpdates(
        selectedUser.username,
        category ? category as any : undefined
      );
      setPatientUpdates(result.updates);
      // Refresh archived entries if currently shown
      if (showArchivedUpdates) {
        const archived = await adminGetArchivedUpdates(
          selectedUser.username,
          category ? category as AllCategory : undefined
        );
        setArchivedUpdates(archived);
      }
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to load patient updates');
    } finally {
      setUpdatesLoading(false);
    }
  };

  const refreshUpdates = async () => {
    if (!selectedUser) return;
    setUpdatesLoading(true);
    try {
      const result = await adminListHealthUpdates(
        selectedUser.username,
        updatesFilter ? updatesFilter as AllCategory : undefined
      );
      setPatientUpdates(result.updates);
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to load patient updates');
    } finally {
      setUpdatesLoading(false);
    }
  };

  const handleAdminFormOpen = (update?: HealthUpdate) => {
    if (update) {
      // Edit mode — pre-fill form
      setEditingUpdate(update);
      const schema = ALL_CATEGORIES.find(c => c.key === update.category) || null;
      setAdminFormCategory(schema);
      setAdminFormData({ ...update.data });
    } else {
      // Add mode
      setEditingUpdate(null);
      setAdminFormCategory(null);
      setAdminFormData({});
    }
    setShowAdminForm(true);
    setUpdatesError('');
  };

  const handleAdminFormCategoryChange = (key: string) => {
    const cat = ALL_CATEGORIES.find(c => c.key === key) || null;
    setAdminFormCategory(cat);
    if (cat) {
      const initial: Record<string, string> = {};
      cat.fields.forEach(f => { initial[f.key] = ''; });
      setAdminFormData(initial);
    } else {
      setAdminFormData({});
    }
  };

  const handleAdminFormFieldChange = (key: string, value: string) => {
    setAdminFormData(prev => ({ ...prev, [key]: value }));
  };

  const isValidDate = (value: string): boolean => {
    if (!value) return true;
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;
    const d = new Date(value + 'T00:00:00');
    return !isNaN(d.getTime())
      && d.getFullYear() === Number(match[1])
      && d.getMonth() + 1 === Number(match[2])
      && d.getDate() === Number(match[3]);
  };

  const adminFormDateFieldsValid = adminFormCategory
    ? adminFormCategory.fields
        .filter(f => f.type === 'date')
        .every(f => isValidDate(adminFormData[f.key] || ''))
    : true;

  const adminFormHasRequired = adminFormCategory
    ? adminFormCategory.fields.filter(f => f.required).every(f => adminFormData[f.key]?.trim())
    : false;

  const adminFormCanSubmit = adminFormCategory && adminFormHasRequired && adminFormDateFieldsValid && !adminFormSaving;

  const handleAdminFormSubmit = async () => {
    if (!adminFormCanSubmit || !selectedUser || !adminFormCategory) return;
    setAdminFormSaving(true);
    setUpdatesError('');
    try {
      if (editingUpdate) {
        await adminUpdateHealthUpdate(selectedUser.username, editingUpdate.updateId, adminFormData);
      } else {
        await adminSubmitHealthUpdate(
          selectedUser.username,
          adminFormCategory.key,
          adminFormData,
          user?.email || user?.username || 'admin'
        );
      }
      setShowAdminForm(false);
      setEditingUpdate(null);
      await refreshUpdates();
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to save update');
    } finally {
      setAdminFormSaving(false);
    }
  };

  const handleAdminDelete = async (update: HealthUpdate) => {
    if (!selectedUser) return;
    if (!window.confirm(`Delete this ${getCategoryLabel(update.category)} entry?`)) return;
    setUpdatesLoading(true);
    setUpdatesError('');
    try {
      await adminDeleteHealthUpdate(selectedUser.username, update.updateId);
      await refreshUpdates();
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to delete update');
    } finally {
      setUpdatesLoading(false);
    }
  };

  const handleViewUpdateAuditLog = async () => {
    if (!selectedUser) return;
    if (showUpdateAuditLog) {
      setShowUpdateAuditLog(false);
      return;
    }
    setUpdateAuditLoading(true);
    try {
      const entries = await adminGetUpdateAuditLog(selectedUser.username);
      setUpdateAuditLog(entries);
      setShowUpdateAuditLog(true);
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to load change log');
    } finally {
      setUpdateAuditLoading(false);
    }
  };

  const handleViewArchivedUpdates = async () => {
    if (!selectedUser) return;
    if (showArchivedUpdates) {
      setShowArchivedUpdates(false);
      return;
    }
    setArchivedLoading(true);
    try {
      const entries = await adminGetArchivedUpdates(
        selectedUser.username,
        updatesFilter ? updatesFilter as AllCategory : undefined
      );
      setArchivedUpdates(entries);
      setShowArchivedUpdates(true);
    } catch (err: any) {
      setUpdatesError(err.message || 'Failed to load archived updates');
    } finally {
      setArchivedLoading(false);
    }
  };

  const getAuditActionColor = (action: string): string => {
    switch (action) {
      case 'added': return 'var(--green)';
      case 'edited': return 'var(--amber)';
      case 'deleted': return 'var(--red)';
      case 'archived': return 'var(--text-light)';
      default: return 'var(--text)';
    }
  };

  const getAuditDetailsSummary = (entry: HealthUpdateAuditEntry): string => {
    const values = Object.values(entry.data).filter(v => v);
    return values[0] || `${Object.keys(entry.data).length} fields`;
  };

  const getCategoryLabel = (key: string): string => {
    const cat = ALL_CATEGORIES.find(c => c.key === key);
    return cat?.label || key;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const categoryLabels: Record<DocumentCategory, { name: string; icon: string }> = {
    'health-summary': { name: 'Health Summary', icon: '📋' },
    'medical': { name: 'Medical Provider', icon: '🏥' },
    'pharmacy': { name: 'Pharmacy', icon: '💊' },
    'personal': { name: 'Personal Files', icon: '📁' },
  };

  const filteredUsers = users.filter((u) => {
    if (filter === 'all') return true;
    if (filter === 'admins') return u.groups.includes('admins');
    if (filter === 'users') return u.groups.includes('users') || u.groups.length === 0;
    return true;
  });

  const totalUsers = users.length;
  const totalAdmins = users.filter((u) => u.groups.includes('admins')).length;

  const getRoleBadge = (groups: string[]) => {
    if (groups.includes('admins')) return 'admin';
    return 'user';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'var(--green)';
      case 'FORCE_CHANGE_PASSWORD':
        return 'var(--amber)';
      case 'UNCONFIRMED':
        return 'var(--red)';
      default:
        return 'var(--text-light)';
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p className="admin-subtitle">Manage users and monitor system usage</p>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <span className="stat-icon">👥</span>
          <div className="stat-content">
            <span className="stat-value">{totalUsers}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">👑</span>
          <div className="stat-content">
            <span className="stat-value">{totalAdmins}</span>
            <span className="stat-label">Admins</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">👤</span>
          <div className="stat-content">
            <span className="stat-value">{totalUsers - totalAdmins}</span>
            <span className="stat-label">Regular Users</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">🔄</span>
          <div className="stat-content">
            <button className="refresh-btn" onClick={fetchUsers} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <p className="error-hint">
            To enable user management, ensure the admin role has permissions to access Cognito User Pool.
          </p>
        </div>
      )}

      <div className="admin-content">
        <div className="users-section">
          <div className="section-header">
            <h2>User Management</h2>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filter === 'admins' ? 'active' : ''}`}
                onClick={() => setFilter('admins')}
              >
                Admins
              </button>
              <button
                className={`filter-btn ${filter === 'users' ? 'active' : ''}`}
                onClick={() => setFilter('users')}
              >
                Users
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading users...</div>
          ) : filteredUsers.length > 0 ? (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr
                    key={u.userId}
                    className={`${selectedUser?.userId === u.userId ? 'selected' : ''} ${
                      u.userId === user?.userId ? 'current-user' : ''
                    }`}
                    onClick={() => setSelectedUser(u)}
                  >
                    <td>
                      {u.email || u.username}
                      {u.userId === user?.userId && (
                        <span className="you-badge">You</span>
                      )}
                    </td>
                    <td>
                      <span className={`role-tag ${getRoleBadge(u.groups)}`}>
                        {getRoleBadge(u.groups)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ color: getStatusColor(u.status) }}
                      >
                        {u.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-users">
              {error ? 'Unable to load users.' : 'No users found.'}
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="user-details">
            <h3>User Details</h3>
            <div className="detail-content">
              <div className="detail-avatar">
                {(selectedUser.email || selectedUser.username).charAt(0).toUpperCase()}
              </div>
              <div className="detail-info">
                <p>
                  <strong>Email:</strong> {selectedUser.email || 'N/A'}
                </p>
                <p>
                  <strong>Username:</strong> {selectedUser.username}
                </p>
                <p>
                  <strong>User ID:</strong>{' '}
                  <span className="user-id">{selectedUser.userId}</span>
                </p>
                <p>
                  <strong>Role:</strong>{' '}
                  <span className={`role-tag ${getRoleBadge(selectedUser.groups)}`}>
                    {getRoleBadge(selectedUser.groups)}
                  </span>
                </p>
                <p>
                  <strong>Groups:</strong>{' '}
                  {selectedUser.groups.length > 0 ? selectedUser.groups.join(', ') : 'None'}
                </p>
                <p>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: getStatusColor(selectedUser.status) }}>
                    {selectedUser.status.replace(/_/g, ' ')}
                  </span>
                </p>
                <p>
                  <strong>Enabled:</strong> {selectedUser.enabled ? 'Yes' : 'No'}
                </p>
                <p>
                  <strong>Created:</strong>{' '}
                  {new Date(selectedUser.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="detail-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleOpenDocModal}
                >
                  Manage Documents
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleOpenEmergencyModal}
                >
                  Emergency Access
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleOpenUpdatesModal}
                >
                  Patient Updates
                </button>
                <a
                  href={`https://ca-central-1.console.aws.amazon.com/cognito/v2/idp/user-pools/ca-central-1_RhLotO9je/users/${selectedUser.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  Manage in AWS Console
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Management Modal */}
      {showDocModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Documents for {selectedUser.email || selectedUser.username}</h2>
              <button className="close-btn" onClick={() => setShowDocModal(false)}>
                &times;
              </button>
            </div>

            <div className="category-tabs">
              {(Object.keys(categoryLabels) as DocumentCategory[]).map((cat) => (
                <button
                  key={cat}
                  className={`category-tab ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat)}
                >
                  <span className="tab-icon">{categoryLabels[cat].icon}</span>
                  {categoryLabels[cat].name}
                </button>
              ))}
            </div>

            <div className="modal-content">
              <div className="upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                  accept={activeCategory === 'health-summary' ? '.xlsx,.pdf,.doc,.docx,.jpg,.jpeg,.png' : '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
                />
                <button
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : `Upload to ${categoryLabels[activeCategory].name}`}
                </button>
              </div>

              {docsError && <div className="docs-error">{docsError}</div>}

              {docsLoading ? (
                <p className="loading-text">Loading documents...</p>
              ) : documents.length > 0 ? (
                <div className="documents-list">
                  {documents.map((doc) => (
                    <div key={doc.key} className="document-item">
                      <div className="doc-icon">{categoryLabels[activeCategory].icon}</div>
                      <div className="doc-info">
                        <span className="doc-name">{doc.name}</span>
                        <span className="doc-meta">
                          {formatFileSize(doc.size)} • {new Date(doc.lastModified).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="doc-actions">
                        {activeCategory === 'health-summary' && doc.name.toLowerCase().endsWith('.xlsx') ? (
                          <>
                            <button className="action-btn" onClick={() => handleViewHealthSummary(doc)}>
                              View
                            </button>
                            <button className="action-btn" onClick={() => handleDownload(doc)}>
                              Download
                            </button>
                          </>
                        ) : (
                          <button className="action-btn" onClick={() => handleDownload(doc)}>
                            View
                          </button>
                        )}
                        <button className="action-btn delete" onClick={() => handleDeleteDoc(doc)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">
                  No {categoryLabels[activeCategory].name.toLowerCase()} documents uploaded yet.
                </p>
              )}

              {/* Health Summary Preview */}
              {previewLoading && (
                <p className="loading-text">Loading health summary preview...</p>
              )}
              {previewData && (previewData as any).sections && (
                <div className="hs-preview">
                  <div className="hs-preview-header">
                    <h3>Health Summary Preview</h3>
                    <button className="action-btn" onClick={() => setPreviewData(null)}>Close Preview</button>
                  </div>
                  {(previewData as any).sections.map((section: any, i: number) => (
                    <div key={i} className="hs-preview-section">
                      <h4>{section.title}</h4>
                      {section.type === 'grid' ? (
                        <div className="hs-preview-grid">
                          {section.data.map((item: any, j: number) => (
                            <div key={j} className="hs-preview-card">
                              <span className="hs-preview-label">{item.field}</span>
                              <span className="hs-preview-value">{item.value || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="hs-preview-table-wrap">
                          <table className="hs-preview-table">
                            <thead>
                              <tr>
                                {section.data.headers.map((h: string, j: number) => (
                                  <th key={j}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {section.data.rows.map((row: string[], j: number) => (
                                <tr key={j}>
                                  {row.map((cell: string, k: number) => (
                                    <td key={k}>{cell || 'N/A'}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Emergency Access Modal */}
      {showEmergencyModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEmergencyModal(false)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Emergency Access for {selectedUser.email || selectedUser.username}</h2>
              <button className="close-btn" onClick={() => setShowEmergencyModal(false)}>
                &times;
              </button>
            </div>

            <div className="modal-content">
              {emergencyError && (
                <div className="docs-error">{emergencyError}</div>
              )}

              {emergencyLoading && !emergencyStatus && (
                <p className="loading-text">Loading emergency access status...</p>
              )}

              {/* Generated code display */}
              {emergencyGenerated && (
                <div style={{
                  background: 'var(--green-bg)',
                  border: '1px solid rgba(40, 167, 69, .3)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.25rem',
                  marginBottom: '1rem',
                }}>
                  <p style={{ color: 'var(--green)', fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                    Emergency code generated. Share these credentials securely with the user.
                  </p>
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Access ID</span>
                      <div style={{ color: 'var(--text)', fontSize: '1.25rem', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '2px' }}>
                        {emergencyGenerated.accessId}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase' }}>PIN</span>
                      <div style={{ color: 'var(--text)', fontSize: '1.25rem', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '2px' }}>
                        {emergencyGenerated.pin}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase' }}>Expires</span>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        {formatDate(emergencyGenerated.expiresAt)}
                      </div>
                    </div>
                  </div>
                  <p style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                    QR URL: {emergencyGenerated.qrUrl}
                  </p>
                </div>
              )}

              {/* Current status */}
              {emergencyStatus && (
                <div style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1rem',
                  marginBottom: '1rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Status</span>
                      <div style={{
                        color: emergencyStatus.hasActiveToken ? 'var(--green)' : 'var(--text-light)',
                        fontSize: '0.9rem', fontWeight: 600,
                      }}>
                        {emergencyStatus.hasActiveToken ? 'Active' : emergencyStatus.token ? emergencyStatus.token.status : 'No code'}
                      </div>
                    </div>
                    {emergencyStatus.token && (
                      <>
                        <div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Access ID</span>
                          <div style={{ color: 'var(--text)', fontFamily: 'monospace', fontSize: '1rem', fontWeight: 600 }}>
                            {emergencyStatus.token.accessId}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Created</span>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            {formatDate(emergencyStatus.token.createdAt)}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Expires</span>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            {formatDate(emergencyStatus.token.expiresAt)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <button
                  className="action-btn"
                  onClick={handleAdminGenerate}
                  disabled={emergencyLoading}
                >
                  {emergencyStatus?.hasActiveToken ? 'Regenerate Code' : 'Generate Code'}
                </button>
                {emergencyStatus?.hasActiveToken && (
                  <button
                    className="action-btn delete"
                    onClick={handleAdminRevoke}
                    disabled={emergencyLoading}
                  >
                    Revoke Code
                  </button>
                )}
                <button
                  className="action-btn"
                  onClick={handleAdminViewAudit}
                  disabled={emergencyLoading}
                >
                  {showEmergencyAudit ? 'Hide Audit Log' : 'View Audit Log'}
                </button>
              </div>

              {/* Audit log */}
              {showEmergencyAudit && (
                <div>
                  <h3 style={{ color: 'var(--text)', fontSize: '1rem', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                    Access Audit Log
                  </h3>
                  {emergencyAuditLog.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="hs-preview-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Action</th>
                            <th>Method</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emergencyAuditLog.map((entry, i) => (
                            <tr key={i}>
                              <td>{formatDate(entry.timestamp)}</td>
                              <td style={{
                                color: entry.action === 'accessed' ? 'var(--amber)' :
                                       entry.action === 'generated' ? 'var(--green)' :
                                       entry.action === 'revoked' ? 'var(--red)' : 'var(--text)',
                                fontWeight: 700,
                              }}>
                                {entry.action}
                              </td>
                              <td>{entry.method || '—'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {entry.ipAddress || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="empty-state">No audit entries yet.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Patient Updates Modal */}
      {showUpdatesModal && selectedUser && (
        <div className="modal-overlay" onClick={() => { setShowUpdatesModal(false); setShowAdminForm(false); setEditingUpdate(null); }}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Patient Updates for {selectedUser.email || selectedUser.username}</h2>
              <button className="close-btn" onClick={() => { setShowUpdatesModal(false); setShowAdminForm(false); setEditingUpdate(null); }}>
                &times;
              </button>
            </div>

            <div className="modal-content">
              {updatesError && (
                <div className="docs-error">{updatesError}</div>
              )}

              {/* Filter + Add Entry row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginRight: '0.5rem' }}>
                    Filter by category:
                  </label>
                  <select
                    value={updatesFilter}
                    onChange={(e) => handleUpdatesFilterChange(e.target.value)}
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                      padding: '0.4rem 0.6rem',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                    }}
                  >
                    <option value="">All Categories</option>
                    {ALL_CATEGORIES.map(cat => (
                      <option key={cat.key} value={cat.key}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="action-btn"
                    onClick={handleViewUpdateAuditLog}
                    disabled={updateAuditLoading}
                  >
                    {showUpdateAuditLog ? 'Hide Change Log' : 'View Change Log'}
                  </button>
                  <button
                    className="action-btn"
                    onClick={handleViewArchivedUpdates}
                    disabled={archivedLoading}
                  >
                    {showArchivedUpdates ? 'Hide Archived' : 'View Archived'}
                  </button>
                  <button
                    className="upload-btn"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                    onClick={() => handleAdminFormOpen()}
                  >
                    + Add Entry
                  </button>
                </div>
              </div>

              {/* Update audit log */}
              {showUpdateAuditLog && (
                <div style={{ marginBottom: '1rem' }}>
                  <h3 style={{ color: 'var(--text)', fontSize: '1rem', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                    Change Log
                  </h3>
                  {updateAuditLog.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="hs-preview-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Action</th>
                            <th>Category</th>
                            <th>Performed By</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {updateAuditLog.map((entry, i) => (
                            <tr key={i}>
                              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.timestamp)}</td>
                              <td style={{ color: getAuditActionColor(entry.action), fontWeight: 700 }}>
                                {entry.action}
                              </td>
                              <td>{getCategoryLabel(entry.category)}</td>
                              <td>{entry.performedBy}</td>
                              <td style={{ fontSize: '0.8rem' }}>
                                {entry.action === 'edited' && entry.previousData ? (
                                  <span>
                                    {Object.keys(entry.data).filter(k => entry.data[k] !== entry.previousData![k]).map(k => (
                                      <div key={k}>
                                        <strong>{k}:</strong>{' '}
                                        <span style={{ color: 'var(--red)', textDecoration: 'line-through' }}>
                                          {entry.previousData![k] || '(empty)'}
                                        </span>
                                        {' → '}
                                        <span style={{ color: 'var(--green)' }}>
                                          {entry.data[k] || '(empty)'}
                                        </span>
                                      </div>
                                    ))}
                                  </span>
                                ) : (
                                  getAuditDetailsSummary(entry)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="empty-state">No change log entries yet.</p>
                  )}
                </div>
              )}

              {/* Archived updates */}
              {showArchivedUpdates && (
                <div style={{ marginBottom: '1rem' }}>
                  <h3 style={{ color: 'var(--text)', fontSize: '1rem', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                    Archived Entries
                  </h3>
                  {archivedUpdates.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {archivedUpdates.map((entry) => (
                        <div key={entry.updateId} style={{
                          background: 'var(--bg)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '1rem',
                          opacity: 0.75,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{
                              background: 'var(--bg)',
                              color: 'var(--text-light)',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              border: '1px solid var(--border)',
                            }}>
                              {getCategoryLabel(entry.category)}
                            </span>
                            <span style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>
                              Archived: {formatDate(entry.archivedAt)}
                            </span>
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '0.5rem',
                            marginBottom: '0.5rem',
                          }}>
                            {Object.entries(entry.data).map(([key, value]) => (
                              <div key={key}>
                                <span style={{ color: 'var(--text-light)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                  {key}
                                </span>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                  {value || 'N/A'}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{
                            borderTop: '1px solid var(--border)',
                            paddingTop: '0.4rem',
                            fontSize: '0.75rem',
                            color: 'var(--text-light)',
                            fontStyle: 'italic',
                          }}>
                            Replaced by newer entry
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state">No archived entries found.</p>
                  )}
                </div>
              )}

              {/* Admin add/edit form */}
              {showAdminForm && (
                <div style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.25rem',
                  marginBottom: '1rem',
                }}>
                  <h3 style={{ color: 'var(--text)', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', marginTop: 0 }}>
                    {editingUpdate ? 'Edit Entry' : 'Add New Entry'}
                  </h3>

                  {/* Category selector (disabled when editing) */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ color: 'var(--text)', fontSize: '0.85rem', fontWeight: 700, display: 'block', marginBottom: '0.35rem' }}>
                      Category <span style={{ color: 'var(--red)' }}>*</span>
                    </label>
                    <select
                      value={adminFormCategory?.key || ''}
                      onChange={(e) => handleAdminFormCategoryChange(e.target.value)}
                      disabled={!!editingUpdate}
                      style={{
                        width: '100%',
                        background: 'var(--white)',
                        border: '1.5px solid var(--border)',
                        color: 'var(--text)',
                        padding: '0.6rem 0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      <option value="">Select a category...</option>
                      {ALL_CATEGORIES.map(cat => (
                        <option key={cat.key} value={cat.key}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dynamic fields */}
                  {adminFormCategory && adminFormCategory.fields.map(field => {
                    const value = adminFormData[field.key] || '';
                    const isDate = field.type === 'date';
                    const showDateError = isDate && value.length > 0 && !isValidDate(value);
                    return (
                      <div key={field.key} style={{ marginBottom: '0.75rem' }}>
                        <label style={{ color: 'var(--text)', fontSize: '0.85rem', fontWeight: 700, display: 'block', marginBottom: '0.35rem' }}>
                          {field.label}
                          {field.required && <span style={{ color: 'var(--red)', marginLeft: '0.25rem' }}>*</span>}
                        </label>
                        <input
                          type={isDate ? 'date' : 'text'}
                          value={value}
                          onChange={(e) => handleAdminFormFieldChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          autoComplete="off"
                          style={{
                            width: '100%',
                            background: 'var(--white)',
                            border: `1.5px solid ${showDateError ? 'var(--red)' : 'var(--border)'}`,
                            color: 'var(--text)',
                            padding: '0.6rem 0.75rem',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontFamily: 'var(--font-body)',
                            boxSizing: 'border-box',
                          }}
                        />
                        {showDateError && (
                          <span style={{ color: 'var(--red)', fontSize: '0.8rem', marginTop: '0.15rem', display: 'block' }}>
                            Please enter a valid date
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Form actions */}
                  {adminFormCategory && (
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                      <button
                        className="action-btn"
                        onClick={() => { setShowAdminForm(false); setEditingUpdate(null); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="upload-btn"
                        style={{ padding: '0.5rem 1.25rem', fontSize: '0.85rem' }}
                        disabled={!adminFormCanSubmit}
                        onClick={handleAdminFormSubmit}
                      >
                        {adminFormSaving ? 'Saving...' : editingUpdate ? 'Save Changes' : 'Add Entry'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {updatesLoading ? (
                <p className="loading-text">Loading patient updates...</p>
              ) : patientUpdates.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {patientUpdates.map((update) => (
                    <div key={update.updateId} style={{
                      background: 'var(--amber-bg)',
                      border: '1px solid rgba(184, 134, 11, .2)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '1rem',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{
                          background: 'var(--amber-bg)',
                          color: 'var(--amber)',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}>
                          {getCategoryLabel(update.category)}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>
                            {formatDate(update.submittedAt)}
                          </span>
                          <button
                            className="action-btn"
                            style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }}
                            onClick={() => handleAdminFormOpen(update)}
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn delete"
                            style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }}
                            onClick={() => handleAdminDelete(update)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                      }}>
                        {Object.entries(update.data).map(([key, value]) => (
                          <div key={key}>
                            <span style={{ color: 'var(--text-light)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                              {key}
                            </span>
                            <div style={{ color: 'var(--text)', fontSize: '0.9rem' }}>
                              {value || 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        borderTop: '1px solid var(--border)',
                        paddingTop: '0.4rem',
                        fontSize: '0.75rem',
                        color: 'var(--text-light)',
                      }}>
                        Signed by: {update.acknowledgement.signedName} on {formatDate(update.acknowledgement.signedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No patient-reported updates found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
